import React, { type FunctionComponent, type PropsWithChildren } from 'react'
import { Text, Marker, type MarkerVariants } from '@retailer-platform/shared-components/src/tds'
import _ from 'lodash'
import { ApprovalRequestState } from '../../api'
import { type ApiApprovalRequest } from '../../api/common'
import { type DomainMessages, useDomainMessages } from '../../utils/domain/intl'
import { getLastUpdatedString } from '../date/date'
import { ReviewAlert } from '../review-alert/ReviewAlert'

export interface ApprovalRequestDetailsInfoBit {
  title: string
  content: (ar: ApiApprovalRequest) => string
}

export type ApprovalRequestDetailsProps = {
  approvalRequest: ApiApprovalRequest
  customInfoBits?: ApprovalRequestDetailsInfoBit[]
  changesNode?: React.ReactNode
}

export const ApprovalRequestDetails: FunctionComponent<ApprovalRequestDetailsProps> = ({
  approvalRequest,
  customInfoBits,
  changesNode,
}) => {
  const defaultInfoBits = useDefaultInfoBits(approvalRequest)
  const infoBits = customInfoBits || [
    defaultInfoBits.Name,
    defaultInfoBits.Tool,
    defaultInfoBits.CreatedBy,
    defaultInfoBits.LastUpdated,
  ]

  const i18n = useDomainMessages({
    details: 'approvalManagementLibrary.arDetails.details',
    comment: 'approvalManagementLibrary.arDetails.comment',
    changes: 'approvalManagementLibrary.arDetails.changes',
  })

  return (
    <>
      <Section title={i18n.details}>
        <TwoColumns
          left={infoBits.map(infoBit => (
            <InfoBit key={infoBit.title} {...infoBit} ar={approvalRequest} />
          ))}
          right={<LatestComment ar={approvalRequest} />}
        />
      </Section>
      <HorizontalLine />
      <Section title={i18n.changes}>{changesNode}</Section>
    </>
  )
}

export const useDefaultInfoBits = (
  approvalRequest: ApiApprovalRequest
): Record<string, ApprovalRequestDetailsInfoBit> => {
  const i18n = useDomainMessages({
    details: 'approvalManagementLibrary.arDetails.details',
    name: 'approvalManagementLibrary.arDetails.name',
    tool: 'approvalManagementLibrary.arDetails.tool',
    createdBy: 'approvalManagementLibrary.arDetails.createdBy',
    lastUpdated: 'approvalManagementLibrary.arDetails.lastUpdated',
    toolName:
      `approvalManagementLibrary.contentTypes.${approvalRequest.contentType}` as DomainMessages,
    system: 'approvalManagementLibrary.general.system',
  })

  return {
    Name: { title: i18n.name, content: ar => ar.name },
    Tool: { title: i18n.tool, content: () => i18n.toolName },
    CreatedBy: { title: i18n.createdBy, content: ar => ar.createdByUser?.givenName || i18n.system },
    LastUpdated: { title: i18n.lastUpdated, content: ar => getLastUpdatedString(ar) },
  }
}

const Section = ({ title, children }: PropsWithChildren<{ title: string }>) => (
  <div css={{ paddingTop: 8, paddingBottom: 8 }}>
    <Text typography="subtitle" css={{ paddingBottom: 8 }}>
      {title}
    </Text>
    <div>{children}</div>
  </div>
)

const getMarkerVariant = (state: ApprovalRequestState) => {
  if (state === ApprovalRequestState.Cancelled) return 'detrimental'
  if (state === ApprovalRequestState.Pending) return 'neutral'
  return 'success'
}

const InfoBit = ({
  title,
  content,
  ar,
}: ApprovalRequestDetailsInfoBit & { ar: ApiApprovalRequest }) => {
  const statusTitle = useDomainMessages({
    status: 'approvalManagementLibrary.searchList.status' as DomainMessages,
  }).status

  return (
    <div css={{ paddingTop: 4, paddingBottom: 4, display: 'flex', flexDirection: 'row' }}>
      <Text typography="bodyEmphasized" css={{ width: 200 }}>
        {title}
      </Text>
      {title === statusTitle ? (
        <div css={{ textTransform: 'capitalize' }}>
          <Marker
            variant={getMarkerVariant(content(ar) as ApprovalRequestState)}
            text={content(ar).toLowerCase()}
          />
        </div>
      ) : (
        <Text typography="bodyRegular">{content(ar)}</Text>
      )}
    </div>
  )
}

const TwoColumns = ({ left, right }: { left: React.ReactNode; right: React.ReactNode }) => (
  <div css={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
    <div css={{ width: '50%' }}>{left}</div>
    <div css={{ width: '50%' }}>{right}</div>
  </div>
)

const LatestComment = ({ ar }: { ar: ApiApprovalRequest }) => {
  const lastReview = _.last(ar.reviews)

  const i18n = useDomainMessages({
    action: `approvalManagementLibrary.reviewAction.${
      lastReview?.reviewContent.action || 'REVIEW_REQUESTED'
    }` as DomainMessages,
  })

  if (!lastReview) {
    return null
  }

  return (
    <ReviewAlert eventType={lastReview.reviewContent.action}>
      <Text typography="bodyRegular">
        {lastReview.reviewedBy.givenName + ' ' + i18n.action.toLowerCase()}
      </Text>
      <Text typography="bodyRegular">{lastReview.reviewContent.comment}</Text>
    </ReviewAlert>
  )
}

const HorizontalLine = () => <div css={{ borderBottom: '1px solid #E0E0E0', margin: '16px 0' }} />
